import { api } from './api';

export interface CourseData {
  curso: string;
  usuario: string;
  email: string;
  data_completou: string;
}

export interface TotalCourse {
  _id: string;
  total: number;
}

export interface Course {
  _id: string;
  title: string;
}

export const getCoursesReports = async () => {
  const response = await api.post('/database/folder/aggregate?strict=true', [
    { $match: { parent: { $exists: false }, type: 'course' } },
  ]);
  return response.data as Course[];
};

export const getCompletedCourses = async (
  filters: {
    courseId?: string;
    dataInicio?: string;
    dataFim?: string;
  },
  rangeRequest: string,
) => {
  const aggregate = [
    {
      $match: {
        actionId: 'finish_course',
      },
    },
    {
      $lookup: {
        from: 'player',
        localField: 'userId',
        foreignField: '_id',
        as: 'p',
      },
    },
    { $unwind: '$p' },
    {
      $lookup: {
        from: 'folder',
        localField: 'attributes.course',
        foreignField: '_id',
        as: 'c',
      },
    },
    { $unwind: '$c' },
    {
      $project: {
        _id: 1,
        usuario: '$p.name',
        curso: '$c.title',
        email: '$p.email',
        data_completou: {
          $dateToString: {
            date: '$time',
            format: '%d/%m/%Y %H:%M:%S',
            timezone: 'America/Sao_Paulo',
          },
        },
        attributes: 1,
        time: 1,
      },
    },
  ];

  const agg = [...aggregate];

  if (filters.courseId || filters.dataInicio || filters.dataFim) {
    const match: any = { $match: {} };

    if (filters.courseId) {
      match['$match']['attributes.course'] = filters.courseId;
    }

    if (filters.dataInicio) {
      match['$match']['time'] = match['$match']['time'] || {};
      match['$match']['time']['$gte'] = { $date: filters.dataInicio };
    }

    if (filters.dataFim) {
      match['$match']['time'] = match['$match']['time'] || {};
      match['$match']['time']['$lte'] = { $date: filters.dataFim };
    }

    agg.unshift(match);
  }

  const response = await api.post(`/database/action_log/aggregate?strict=true`, agg, {
    headers: {
      Range: rangeRequest,
    },
  });

  return {
    data: response.data as CourseData[],
    contentRange: response.headers['content-range'] as string,
  };
};

export const getTotalCourses = async (filters: { courseId?: string; dataInicio?: string; dataFim?: string }) => {
  const aggregate2 = [
    {
      $match: {
        actionId: 'finish_course',
      },
    },
    {
      $lookup: {
        from: 'folder',
        localField: 'attributes.course',
        foreignField: '_id',
        as: 'c',
      },
    },
    { $unwind: '$c' },
    { $group: { _id: '$c.title', total: { $sum: 1 } } },
  ];

  const agg = [...aggregate2];

  if (filters.courseId || filters.dataInicio || filters.dataFim) {
    const match: any = { $match: {} };

    if (filters.courseId) {
      match['$match']['attributes.course'] = filters.courseId;
    }

    if (filters.dataInicio) {
      match['$match']['time'] = match['$match']['time'] || {};
      match['$match']['time']['$gte'] = { $date: filters.dataInicio };
    }

    if (filters.dataFim) {
      match['$match']['time'] = match['$match']['time'] || {};
      match['$match']['time']['$lte'] = { $date: filters.dataFim };
    }

    agg.unshift(match);
  }

  const response = await api.post('/database/action_log/aggregate?strict=true', agg);
  return response.data as TotalCourse[];
};

const FULL_RESULT_RANGE = { headers: { Range: 'items=0-1000000' } };

export const getRanking = async (id: string) => {
  const response = await api.post(
    `/leaderboard/${id}/leader/aggregate?period=-10y%3B-0d%2B&live=true`,
    [
      {
        $lookup: {
          from: 'player',
          localField: 'player',
          foreignField: '_id',
          as: 'info',
        },
      },
      {
        $unwind: '$info',
      },
    ],
    FULL_RESULT_RANGE,
  );
  return response.data as any[];
};

export const getInactivePlayers = async () => {
  const payload = [
    {
      $match: {
        name: {
          $exists: true,
        },
      },
    },
    {
      $lookup: {
        from: 'action_log',
        let: {
          player: '$_id',
        },
        pipeline: [
          {
            $match: {
              $expr: {
                $eq: ['$userId', '$$player'],
              },
              actionId: 'login',
            },
          },
          {
            $limit: 1,
          },
        ],
        as: 'login',
      },
    },
    {
      $match: {
        login: {
          $size: 0,
        },
      },
    },
    {
      $sort: {
        name: 1,
      },
    },
  ];

  const response = await api.post('/database/player/aggregate', payload, FULL_RESULT_RANGE);
  return response.data as any[];
};

export const getTotalPlayers = async () => {
  const response = await api.post('/database/player/aggregate', [
    {
      $count: 'total',
    },
  ]);
  return response.data[0]?.total || 0;
};

export const getDailyAccessData = async (startDate: string, endDate: string, period: any) => {
  const match: any = {
    actionId: 'login',
    'attributes.first_of_day': { $exists: true },
  };
  if (startDate && endDate) {
    match.time = { $gte: { $date: startDate }, $lte: { $date: endDate } };
  } else if (period.start && period.end) {
    match.time = { $gte: { $date: period.start }, $lte: { $date: period.end } };
  }

  const response = await api.post('/database/action_log/aggregate', [
    { $match: match },
    {
      $addFields: {
        yearMonthDay: {
          $dateToString: {
            format: '%Y-%m-%d',
            date: '$time',
            timezone: '-0300',
          },
        },
        weekDay: {
          $dayOfWeek: {
            date: '$time',
            timezone: '-0300',
          },
        },
        yearDay: {
          $dayOfYear: {
            date: '$time',
            timezone: '-0300',
          },
        },
      },
    },
    {
      $match: {
        weekDay: { $nin: [1, 7] },
        yearDay: { $nin: [306, 319] },
      },
    },
    {
      $group: {
        _id: { player: '$userId', time: '$yearMonthDay' },
      },
    },
    {
      $lookup: {
        from: 'player',
        localField: '_id.player',
        foreignField: '_id',
        as: 'p',
      },
    },
    { $unwind: '$p' },
    {
      $group: {
        _id: '$_id.time',
        total: { $sum: 1 },
      },
    },
    {
      $project: {
        _id: 0,
        date: '$_id',
        total: 1,
      },
    },
    { $sort: { date: 1 } },
  ]);
  return response.data.map((item: any) => ({ date: item.date, total: item.total }));
};

export const getHourlyAccessData = async (startDate: string, endDate: string, period: any) => {
  const match: any = {
    actionId: 'login',
    'attributes.first_of_day': { $exists: true },
  };
  if (startDate && endDate) {
    match.time = { $gte: { $date: startDate }, $lte: { $date: endDate } };
  } else if (period.start && period.end) {
    match.time = { $gte: { $date: period.start }, $lte: { $date: period.end } };
  }

  const response = await api.post('/database/action_log/aggregate', [
    { $match: match },
    {
      $addFields: {
        yearMonthDay: {
          $dateToString: {
            format: '%Y-%m-%d %H:00',
            date: '$time',
            timezone: '-0300',
          },
        },
        weekDay: {
          $dayOfWeek: {
            date: '$time',
            timezone: '-0300',
          },
        },
        yearDay: {
          $dayOfYear: {
            date: '$time',
            timezone: '-0300',
          },
        },
      },
    },
    {
      $match: {
        weekDay: { $nin: [1, 7] },
        yearDay: { $nin: [306, 319] },
      },
    },
    {
      $group: {
        _id: { player: '$userId', time: '$yearMonthDay' },
      },
    },
    {
      $lookup: {
        from: 'player',
        localField: '_id.player',
        foreignField: '_id',
        as: 'p',
      },
    },
    { $unwind: '$p' },
    {
      $group: {
        _id: '$_id.time',
        total: { $sum: 1 },
      },
    },
    {
      $project: {
        _id: 0,
        date: '$_id',
        total: 1,
      },
    },
    { $sort: { date: 1 } },
  ]);
  return response.data.map((item: any) => ({ date: item.date, total: item.total }));
};

export const getChallengesData = async (startDate: string, endDate: string, period: any, hourly = false) => {
  const match: any = {
    type: 1,
    total: 1,
  };
  if (startDate && endDate) {
    match.time = { $gte: { $date: startDate }, $lte: { $date: endDate } };
  } else if (period.start && period.end) {
    match.time = { $gte: { $date: period.start }, $lte: { $date: period.end } };
  }

  const format = hourly ? '%Y-%m-%d %H:00' : '%Y-%m-%d';

  const response = await api.post('/database/achievement/aggregate', [
    { $match: match },
    {
      $group: {
        _id: { type: '$type', time: { $dateToString: { format, date: '$time', timezone: '-0300' } } },
        total: { $sum: 1 },
      },
    },
    { $project: { _id: 1, type: '$_id.type', total: 1, data: { $toDate: '$_id.time' } } },
    { $match: { type: 1 } },
    { $sort: { data: 1 } },
  ]);
  return response.data.map((item: any) => ({ _id: { time: item._id.time }, total: item.total }));
};

export const getTotalCompletedCourses = async (startDate: string, endDate: string, period: any) => {
  const match: any = {
    actionId: 'finish_course',
  };
  if (startDate && endDate) {
    match.time = { $gte: { $date: startDate }, $lte: { $date: endDate } };
  } else if (period.start && period.end) {
    match.time = { $gte: { $date: period.start }, $lte: { $date: period.end } };
  }

  const response = await api.post('/database/action_log/aggregate', [
    { $match: match },
    {
      $lookup: {
        from: 'folder',
        localField: 'attributes.course',
        foreignField: '_id',
        as: 'f',
      },
    },
    { $unwind: '$f' },
    {
      $group: {
        _id: '$attributes.course',
        total: { $sum: 1 },
        curso: { $first: '$f.title' },
      },
    },
  ]);
  return response.data;
};

export const getTotalGames = async (startDate: string, endDate: string, period: any) => {
  const match: any = {
    actionId: 'content',
    'attributes.type': { $exists: true, $regex: 'game_', $options: 'gi' },
  };
  if (startDate && endDate) {
    match.time = { $gte: { $date: startDate }, $lte: { $date: endDate } };
  } else if (period.start && period.end) {
    match.time = { $gte: { $date: period.start }, $lte: { $date: period.end } };
  }

  const response = await api.post('/database/action_log/aggregate', [
    { $match: match },
    {
      $group: {
        _id: '$attributes.type',
        total: { $sum: 1 },
      },
    },
    { $sort: { total: -1 } },
  ]);
  return response.data;
};

export const getMonthlyActiveUsers = async (startDate: string, endDate: string, period: any) => {
  const match: any = {
    actionId: 'login',
    'attributes.first_of_day': { $exists: true },
  };
  if (startDate && endDate) {
    match.time = { $gte: { $date: startDate }, $lte: { $date: endDate } };
  } else if (period.start && period.end) {
    match.time = { $gte: { $date: period.start }, $lte: { $date: period.end } };
  }

  const response = await api.post('/database/action_log/aggregate', [
    { $match: match },
    {
      $group: {
        _id: null,
        total: { $sum: 1 },
      },
    },
  ]);
  return response.data[0]?.total || 0;
};

export const getMonthlyActiveUsersUniques = async (startDate: string, endDate: string, period: any) => {
  const match: any = {
    actionId: 'login',
    'attributes.first_of_day': { $exists: true },
  };
  if (startDate && endDate) {
    match.time = { $gte: { $date: startDate }, $lte: { $date: endDate } };
  } else if (period.start && period.end) {
    match.time = { $gte: { $date: period.start }, $lte: { $date: period.end } };
  }

  const response = await api.post('/database/action_log/aggregate', [
    { $match: match },
    {
      $group: {
        _id: '$userId',
        total: { $sum: 1 },
      },
    },
    {
      $group: {
        _id: null,
        total: { $sum: 1 },
      },
    },
  ]);
  return response.data[0]?.total || 0;
};

export const getMonthlyChallenges = async (startDate: string, endDate: string, period: any) => {
  const match: any = {};
  if (startDate && endDate) {
    match.time = { $gte: { $date: startDate }, $lte: { $date: endDate } };
  } else if (period.start && period.end) {
    match.time = { $gte: { $date: period.start }, $lte: { $date: period.end } };
  }

  const response = await api.post('/database/achievement/aggregate', [
    { $match: match },
    {
      $group: {
        _id: '$type',
        total: { $sum: 1 },
      },
    },
    { $match: { _id: 1 } },
  ]);
  return response.data[0]?.total || 0;
};
